import React, { useEffect, useMemo, useRef } from 'react';
import { Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ViewManager } from '../core/ComponentManager';
import { ReduxHelper } from '../core/ReduxHelper';
import { LocaleProvider } from './LocaleProvider';

const mutationCallback = (root: Root, domId: string, rootViewName: string): MutationCallback => (mutationList, _observer) => {
  for (const mutation of mutationList) {
    if (mutation.type === 'childList') {
      for (const removedNode of mutation.removedNodes) {
        if (removedNode.nodeType === Node.ELEMENT_NODE) {
          let element = removedNode as Element;

          if (element.getAttribute('data-mdf-root-id') !== 'application_root') {
            element = element.querySelector(`#${domId}`);
          }

          // Check if this IS the app root, or CONTAINS the app root
          if (element?.getAttribute('data-mdf-root-id') === 'application_root') {
            console.warn(`mutationCallback(${rootViewName}): An application root node has been removed.`, removedNode);
            root.unmount();
          }
        }
      }
    }
  }
};

export interface IApplicationProps {
  mountDOMNode?: HTMLElement;
  onMount?: (observer: MutationObserver) => void;
  onUnmount?: () => void;
  reducer?: (state: any, action: any) => any;
  root?: Root;
  rootViewName: string;
  store?: any;
}

const Application = (props: IApplicationProps) => {
  const shouldLog = !!(window?.localStorage?.getItem('mdfDebugAll'));
  const appNode = useRef(null);
  const observer = useRef<MutationObserver>(null);
  const { mountDOMNode, onMount, onUnmount, reducer, root, rootViewName, store } = props;

  const RootViewComponent = useMemo(() => ViewManager.getViewFast(rootViewName), [rootViewName]);

  const actualStore = useMemo(() => {
    if (store) {
      return store;
    }
    else if (reducer) {
      return ReduxHelper.createStore(reducer);
    }
    else {
      console.error('Application(): No store or reducer was provided.');
    }
  }, []);

  if (shouldLog) {
    console.warn('Application(): Entering. RootViewComponent =', RootViewComponent);
  }

  useEffect(() => {
    console.warn(`Application.useEffect(): Mounting ${rootViewName} at node ${mountDOMNode.id}`);

    // Create the mutation observer once - useEffect can sometimes get called multiple times.
    if (!observer.current) {
      observer.current = new MutationObserver(mutationCallback(root, mountDOMNode.id, rootViewName));
    }

    onMount?.(observer.current);

    return () => {
      console.warn(`Application.useEffect(): Unmounting ${rootViewName} from node ${mountDOMNode.id}`);

      if (observer.current) {
        observer.current.disconnect();
      }

      onUnmount?.();
    };
  }, []);

  return (
    <Provider store={actualStore}>
      <LocaleProvider>
        <RootViewComponent ref={appNode}/>
      </LocaleProvider>
    </Provider>
  );
};

export default Application;
